import React from 'react';
import { BarChart3, Target, Shield, AlertTriangle, CheckCircle } from 'lucide-react';

interface ProjectStats {
  totalProcessSteps: number;
  totalRisks: number;
  totalControls: number;
  totalMappings: number;
  completeSteps: number;
  incompleteSteps: number;
}

interface MappingStatsProps {
  stats: ProjectStats;
}

export const MappingStats: React.FC<MappingStatsProps> = ({ stats }) => {
  const completionPercentage = stats.totalProcessSteps > 0 
    ? Math.round((stats.completeSteps / stats.totalProcessSteps) * 100)
    : 0;

  const riskControlRatio = stats.totalRisks > 0 
    ? (stats.totalControls / stats.totalRisks).toFixed(2)
    : 'N/A';

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <div className="flex items-center gap-2 mb-4">
        <BarChart3 className="h-5 w-5 text-[#57C8F4]" />
        <h3 className="text-lg font-semibold text-[#221551]">Project Mapping Statistics</h3>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {/* Total Process Steps */}
        <div className="bg-gray-50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-[#221551] mb-1">
            {stats.totalProcessSteps}
          </div>
          <div className="text-sm text-gray-600">Process Steps</div>
        </div>

        {/* Total Risks */}
        <div className="bg-yellow-50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-yellow-700 mb-1">
            {stats.totalRisks}
          </div>
          <div className="text-sm text-yellow-600">Risks Identified</div>
        </div>

        {/* Total Controls */}
        <div className="bg-blue-50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-blue-700 mb-1">
            {stats.totalControls}
          </div>
          <div className="text-sm text-blue-600">Controls Mapped</div>
        </div>

        {/* Total Mappings */}
        <div className="bg-purple-50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-purple-700 mb-1">
            {stats.totalMappings}
          </div>
          <div className="text-sm text-purple-600">Total Mappings</div>
        </div>

        {/* Completion Rate */}
        <div className="bg-green-50 rounded-lg p-4 text-center">
          <div className="flex items-center justify-center gap-1 mb-1">
            <CheckCircle className="h-5 w-5 text-green-600" />
            <span className="text-2xl font-bold text-green-700">{completionPercentage}%</span>
          </div>
          <div className="text-sm text-green-600">Complete</div>
          <div className="text-xs text-gray-500 mt-1">
            {stats.completeSteps} of {stats.totalProcessSteps}
          </div>
        </div>

        {/* Needs Attention */}
        <div className="bg-orange-50 rounded-lg p-4 text-center">
          <div className="flex items-center justify-center gap-1 mb-1">
            <AlertTriangle className="h-5 w-5 text-orange-600" />
            <span className="text-2xl font-bold text-orange-700">{stats.incompleteSteps}</span>
          </div>
          <div className="text-sm text-orange-600">Need Attention</div>
          <div className="text-xs text-gray-500 mt-1">Steps incomplete</div>
        </div>
      </div>

      {/* Additional Metrics */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-gray-600">Risk-Control Ratio:</span>
            <span className="font-medium text-[#221551]">1:{riskControlRatio}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-600">Avg Mappings/Step:</span>
            <span className="font-medium text-[#221551]">
              {stats.totalProcessSteps > 0 ? (stats.totalMappings / stats.totalProcessSteps).toFixed(1) : '0'}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-600">Coverage:</span>
            <span className="font-medium text-[#221551]">
              {stats.totalMappings > 0 ? 'Active' : 'None'}
            </span>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mt-4">
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="text-gray-600">Overall Completion</span>
          <span className="text-[#221551] font-medium">{completionPercentage}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-gradient-to-r from-[#57C8F4] to-[#221551] h-2 rounded-full transition-all duration-300"
            style={{ width: `${completionPercentage}%` }}
          ></div>
        </div>
      </div>
    </div>
  );
};